// crewbit-admin/src/components/money/bootstrap/BootstrapWizard.tsx
//
// Tier 2-UI Phase A §WorldClass.4 — admin parallel of mobile BootstrapWizard.
// Mirrors mobile body decisions per `feedback_admin_pair_must_match_mobile_body`.
//
// Two contexts (Decision §A.D1):
//   - SetupGuide item context (mounted from SetupWizardItem.tsx)
//   - Settings hub re-entry (mounted from /money/settings Landing)
//
// Error dispatch (per ack #1 in Cowork stop+report):
//   - INVALID_PROVINCE → ProvincePicker error state
//   - INVALID_TRADE → TradePicker error state
//   - INVALID_CRA_BN_FORMAT → CRA BN input error state
//   - NO_ACTIVE_BUSINESS / STALE_PUBLIC_USER_ID / FORBIDDEN / UNKNOWN /
//     network → top-level wizard error block
//
// Sentry capture is owned by callMoneyRpc at the wrapper boundary; this
// wizard does NOT add explicit Sentry.captureException calls.

import { useState } from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useBootstrapDefaults } from '../../../hooks/useBootstrapDefaults';
import { lookupErrorCopy } from '../../../lib/errorMap';
import {
  industryToTradeKey,
  isFallbackTradeKey,
  type TradeKey,
} from '../../../lib/industryToTradeKey';
import { admin, palette, radii, spacing } from '../../../styles/tokens';
import type { BootstrapDefaultsDerived } from '../../../types/onboarding';
import ProvincePicker, { type ProvinceCode } from './ProvincePicker';
import TradePicker from './TradePicker';
import BootstrapReasoningPanel from './BootstrapReasoningPanel';

interface Props {
  businessId: string;
  initialProvince?: ProvinceCode | null;
  subIndustryId: string | null;
  onSuccess: (derived: BootstrapDefaultsDerived) => void;
  onSkip: () => void;
}

const CRA_BN_REGEX = /^[0-9]{9}$/;

interface FieldErrors {
  province?: string;
  trade?: string;
  craBn?: string;
  topLevel?: string;
}

export default function BootstrapWizard({
  businessId,
  initialProvince = null,
  subIndustryId,
  onSuccess,
  onSkip,
}: Props) {
  const bootstrap = useBootstrapDefaults();

  const initialTradeKey = industryToTradeKey(subIndustryId);
  const initialIsFallback = isFallbackTradeKey(subIndustryId);

  const [province, setProvince] = useState<ProvinceCode | null>(initialProvince);
  const [craBn, setCraBn] = useState('');
  const [tradeKey, setTradeKey] = useState<TradeKey>(initialTradeKey);
  const [tradeIsFallback, setTradeIsFallback] = useState(initialIsFallback);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [derived, setDerived] = useState<BootstrapDefaultsDerived | null>(null);

  const canSubmit = province !== null && !submitting;

  const handleProvinceChange = (next: ProvinceCode) => {
    setProvince(next);
    if (errors.province) setErrors((prev) => ({ ...prev, province: undefined }));
  };

  const handleTradeChange = (next: TradeKey) => {
    setTradeKey(next);
    setTradeIsFallback(false);
    if (errors.trade) setErrors((prev) => ({ ...prev, trade: undefined }));
  };

  const handleCraBnChange = (raw: string) => {
    const cleaned = raw.replace(/[^0-9]/g, '').slice(0, 9);
    setCraBn(cleaned);
    if (errors.craBn) setErrors((prev) => ({ ...prev, craBn: undefined }));
  };

  const handleSubmit = async () => {
    if (!province) return;

    const trimmedBn = craBn.trim();
    if (trimmedBn.length > 0 && !CRA_BN_REGEX.test(trimmedBn)) {
      setErrors({
        craBn: lookupErrorCopy('bootstrap_business_defaults', 'INVALID_CRA_BN_FORMAT'),
      });
      return;
    }

    setErrors({});
    setSubmitting(true);

    const result = await bootstrap({
      businessId,
      province,
      tradeKey,
      craBusinessNumber: trimmedBn.length === 0 ? null : trimmedBn,
      taxYear: null,
    });

    setSubmitting(false);

    if (!result.success) {
      const code = result.error.code;
      const copy = lookupErrorCopy('bootstrap_business_defaults', code);
      if (code === 'INVALID_PROVINCE') setErrors({ province: copy });
      else if (code === 'INVALID_TRADE') setErrors({ trade: copy });
      else if (code === 'INVALID_CRA_BN_FORMAT') setErrors({ craBn: copy });
      else setErrors({ topLevel: copy });
      return;
    }

    setDerived(result.data.derived);
  };

  // ── Success state ─────────────────────────────────────────────────────────
  if (derived) {
    return (
      <div style={wrap}>
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
          <CheckCircle2 size={26} color={palette.green} />
          <div style={{ fontSize: 20, fontWeight: 700, color: palette.g800 }}>
            Smart defaults loaded
          </div>
        </div>
        <div style={{ fontSize: 14, color: palette.g600 }}>
          We&apos;ve set up HST, mileage, and payment terms based on your province and trade.
          You can adjust everything in Settings anytime.
        </div>
        <BootstrapReasoningPanel derived={derived} />
        <div>
          <button type="button" onClick={() => onSuccess(derived)} style={primaryBtn}>
            Continue
          </button>
        </div>
      </div>
    );
  }

  // ── Form state ────────────────────────────────────────────────────────────
  return (
    <div style={wrap}>
      <div style={{ fontSize: 20, fontWeight: 700, color: palette.g800 }}>
        Quick tax setup
      </div>
      <div style={{ fontSize: 14, color: palette.g600 }}>
        Adds smart defaults so you don&apos;t have to configure HST rates, mileage,
        payment terms, etc. Takes about 90 seconds.
      </div>

      <Field label="Province">
        <ProvincePicker
          value={province}
          onChange={handleProvinceChange}
          error={errors.province ?? null}
          disabled={submitting}
        />
      </Field>

      <Field label="CRA Business Number (optional)">
        <input
          type="text"
          inputMode="numeric"
          maxLength={9}
          value={craBn}
          onChange={(e) => handleCraBnChange(e.target.value)}
          placeholder="123456789"
          disabled={submitting}
          aria-label="CRA business number"
          style={{
            width: '100%',
            height: 40,
            padding: spacing.md,
            borderRadius: radii.md,
            border: `1px solid ${errors.craBn ? palette.red : palette.g300}`,
            background: palette.white,
            color: palette.g800,
            fontSize: 14,
            fontFamily: 'inherit',
          }}
        />
        {errors.craBn ? (
          <div style={{ marginTop: spacing.xs, color: palette.red, fontSize: 12 }}>
            {errors.craBn}
          </div>
        ) : (
          <div style={{ marginTop: spacing.xs, color: palette.g500, fontSize: 12 }}>
            9 digits — the part before &quot;RT0001&quot;. You can add this later in Settings.
          </div>
        )}
      </Field>

      <Field label="Trade">
        <TradePicker
          value={tradeKey}
          onChange={handleTradeChange}
          isFallback={tradeIsFallback}
          error={errors.trade ?? null}
          disabled={submitting}
        />
      </Field>

      {errors.topLevel ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: spacing.sm,
            background: palette.redWash,
            borderRadius: radii.md,
            padding: spacing.md,
            color: palette.red,
            fontSize: 13,
          }}
        >
          <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{errors.topLevel}</span>
        </div>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
        <button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={!canSubmit}
          style={{
            ...primaryBtn,
            opacity: canSubmit ? 1 : 0.5,
            cursor: canSubmit ? 'pointer' : 'not-allowed',
          }}
        >
          {submitting ? 'Setting up…' : 'Set up now'}
        </button>
        <button
          type="button"
          onClick={onSkip}
          disabled={submitting}
          style={{
            background: 'transparent',
            border: 'none',
            color: palette.accent,
            fontSize: 13,
            fontWeight: 600,
            cursor: submitting ? 'not-allowed' : 'pointer',
            padding: spacing.sm,
            fontFamily: 'inherit',
          }}
        >
          Skip — I&apos;ll add it later
        </button>
      </div>
    </div>
  );
}

const wrap: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.lg,
  background: palette.white,
  border: `1px solid ${admin.border}`,
  borderRadius: admin.cardRadius,
  padding: spacing.xl,
  maxWidth: 560,
};

const primaryBtn: React.CSSProperties = {
  height: 44,
  background: palette.accent,
  color: palette.white,
  border: 'none',
  borderRadius: radii.md,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: palette.g700,
          letterSpacing: 0.2,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}
